// Progresso (DESIGN §5.16): barra contínua, barra segmentada (séries/semanas) e anel (descanso).
// Sempre acompanhada do número por extenso ao lado ou acima.
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";

export interface PropsBarra {
  valor: number;
  max: number;
  /** Texto para o leitor de tela ("2 de 3 treinos"). */
  textoAcessivel: string;
  sobreTinta?: boolean;
  /** Meta batida: preenchimento ok. */
  batida?: boolean;
  className?: string;
}

/** Barra 8 px que enche ao entrar (900 ms). */
export function BarraProgresso({ valor, max, textoAcessivel, sobreTinta, batida, className }: PropsBarra) {
  const alvo = max > 0 ? Math.min(100, Math.max(0, (valor / max) * 100)) : 0;
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const q = requestAnimationFrame(() => setLargura(alvo));
    return () => cancelAnimationFrame(q);
  }, [alvo]);
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={valor}
      aria-valuetext={textoAcessivel}
      className={cn("h-2 overflow-hidden rounded-full", sobreTinta ? "bg-tinta-2" : "bg-recuo", className)}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-(--gs-dur-barra) ease-gs",
          batida ? "bg-ok" : sobreTinta ? "bg-sobre-tinta" : "bg-tinta",
        )}
        style={{ width: `${largura}%` }}
      />
    </div>
  );
}

export type EstadoSegmento = "feito" | "atual" | "restante";

/** Segmentos (séries do exercício, semanas do mês). Feito tinta (com corte se largo), atual marca, restante fio. */
export function BarraSegmentada({
  segmentos,
  textoAcessivel,
  comCorte,
  sobreTinta,
  className,
}: {
  segmentos: EstadoSegmento[];
  textoAcessivel: string;
  /** Aplica `corte` nos feitos (semanas; só quando cada segmento tem ≥ 24 px). */
  comCorte?: boolean;
  sobreTinta?: boolean;
  className?: string;
}) {
  const feitos = segmentos.filter((s) => s === "feito").length;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={segmentos.length}
      aria-valuenow={feitos}
      aria-valuetext={textoAcessivel}
      className={cn("flex gap-1", className)}
    >
      {segmentos.map((s, i) => (
        <span
          key={i}
          aria-hidden
          className={cn(
            "h-2.5 flex-1 rounded-[3px]",
            s === "feito" && (sobreTinta ? "bg-sobre-tinta" : "bg-tinta"),
            s === "feito" && comCorte && "corte",
            s === "atual" && "bg-marca",
            s === "restante" && (sobreTinta ? "bg-tinta-2" : "bg-fio"),
          )}
        />
      ))}
    </div>
  );
}

/** Anel do descanso: SVG 280 px, trilho tinta-2, arco marca, anima dashoffset 1 s por tick. */
export function AnelProgresso({
  fracao,
  tamanho = 280,
  espessura = 8,
  children,
  className,
}: {
  /** 0..1 do tempo restante. */
  fracao: number;
  tamanho?: number;
  espessura?: number;
  children?: ReactNode;
  className?: string;
}) {
  const r = (tamanho - espessura) / 2;
  const circ = 2 * Math.PI * r;
  const f = Math.min(1, Math.max(0, fracao));
  return (
    <div
      className={cn("relative grid place-items-center", className)}
      style={{ width: tamanho, height: tamanho }}
    >
      <svg
        width={tamanho}
        height={tamanho}
        viewBox={`0 0 ${tamanho} ${tamanho}`}
        aria-hidden
        className="-rotate-90"
      >
        <circle
          cx={tamanho / 2}
          cy={tamanho / 2}
          r={r}
          fill="none"
          strokeWidth={espessura}
          className="stroke-tinta-2"
        />
        <circle
          cx={tamanho / 2}
          cy={tamanho / 2}
          r={r}
          fill="none"
          strokeWidth={espessura}
          strokeLinecap="butt"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - f)}
          className="stroke-marca transition-[stroke-dashoffset] duration-1000 ease-linear"
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  );
}
